import { useLayoutEffect, useRef, useState } from 'react'

import { DEFAULT_TOOLBAR_HEIGHT } from './stickyOffsets'

/**
 * Track a sticky Results toolbar above a table + (optionally) the
 * grouped header row inside it, and expose the per-row sticky `top:`
 * values that pin both <thead> rows flush below the toolbar when the
 * page scrolls.
 *
 * Mirrors the ResizeObserver-driven measurement pattern used by the
 * inspector's `useMeasuredHeight` helper in
 * `components/shared/DataViewTable/stickyOffsets.ts`. We re-
 * implement (rather than import) so admin chrome assumptions can
 * evolve independently of the inspector's collapsing brand header.
 *
 * Usage:
 *   const {
 *     toolbarRef, groupHeaderRef,
 *     groupHeaderTop, columnHeaderTop,
 *   } = useStickyTableHead()
 *
 *   // Attach toolbarRef to the sticky toolbar <Box>.
 *   // Attach groupHeaderRef to the optional group-header <TableRow>;
 *   //   safe to leave unattached when no group row renders - height
 *   //   stays 0 and columnHeaderTop collapses to toolbarHeight.
 *   // Spread { position: 'sticky', top: groupHeaderTop, zIndex: ... }
 *   //   onto every cell of the group-header row, and the column-header
 *   //   equivalent onto every column-header cell.
 */
export function useStickyTableHead(): {
  toolbarRef: React.RefObject<HTMLDivElement>
  groupHeaderRef: React.RefObject<HTMLTableRowElement>
  toolbarHeight: number
  groupHeaderHeight: number
  groupHeaderTop: number
  columnHeaderTop: number
} {
  // useRef<T>(null) returns RefObject<T> (non-nullable T param), which is
  // what MUI ref props accept. React allows null at runtime regardless.
  const toolbarRef = useRef<HTMLDivElement>(null)
  const groupHeaderRef = useRef<HTMLTableRowElement>(null)
  const [toolbarHeight, setToolbarHeight] = useState(DEFAULT_TOOLBAR_HEIGHT)
  const [groupHeaderHeight, setGroupHeaderHeight] = useState(0)

  // Effect has no deps array on purpose - the underlying DOM node can
  // swap between renders (toolbar visibility ties to job.status), so we
  // re-bind the observer each pass. Same pattern as the inspector's
  // `useMeasuredHeight`.
  useLayoutEffect(() => {
    const node = toolbarRef.current
    if (!node) return
    const measure = () =>
      setToolbarHeight(node.getBoundingClientRect().height)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  })

  // Group-header row mounts/unmounts when the report toggles
  // `groupHeaderData` between defined / undefined, so the no-deps
  // re-bind handles both mount and unmount cleanly.
  useLayoutEffect(() => {
    const node = groupHeaderRef.current
    if (!node) {
      setGroupHeaderHeight(0)
      return
    }
    const measure = () =>
      setGroupHeaderHeight(node.getBoundingClientRect().height)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  })

  return {
    toolbarRef,
    groupHeaderRef,
    toolbarHeight,
    groupHeaderHeight,
    groupHeaderTop: toolbarHeight,
    columnHeaderTop: toolbarHeight + groupHeaderHeight,
  }
}
